import React, { useState } from "react";
import { useSelector } from "react-redux";
import { ContentCopy } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import {
  hydrateLayer,
  selectDraftConfigEntities,
  transformStylePayload,
  useUpdateLayerConfigMutation,
  useUpdateSublayerConfigMutation,
} from "fond/api";
import { useEditorContext } from "fond/styleEditor/Editor/EditorProvider";
import { Style } from "fond/types";
import { LayerConfig, LayerConfigHydrated, SublayerConfig, SublayerConfigHydrated } from "fond/types/ProjectLayerConfig";
import { Modal } from "fond/widgets";

import CopyStylesForm, { ICopyStylesFormData } from "./CopyStylesForm";

interface IProps {
  layerConfig: LayerConfig | SublayerConfig;
  mapLayerConfigId: string;
  onClose(): void;
}

const CopyStyles: React.FC<IProps> = ({ layerConfig, mapLayerConfigId, onClose }: IProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const draft = useSelector(selectDraftConfigEntities);
  const [updateLayerConfig] = useUpdateLayerConfigMutation();
  const [updateSubLayerConfig] = useUpdateSublayerConfigMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { copyStylesSourceProject, copyStylesSourceVersion } = useEditorContext();
  const [formValues, setFormValues] = useState<ICopyStylesFormData>({
    selectedLayerStyles: [],
    OverwriteStyles: false,
    Project: copyStylesSourceProject,
    Version: copyStylesSourceVersion,
  });

  const handleOnCancel = () => {
    onClose();
  };

  /**
   * Applies the selected styles to the layer/sublayer.
   */
  const handleOnSubmit = async () => {
    const newLayer = hydrateLayer(draft, layerConfig);
    const selectedStyles = formValues.selectedLayerStyles || [];
    const selectedStylesPayload = selectedStyles.map((style, styleIndex) => ({
      ...transformStylePayload(style, styleIndex, true),
      ConfigurationType: layerConfig.Type,
      ConfigurationID: layerConfig.ID,
    })) as Style[];

    // If overwriting, replace all styles; otherwise, append to existing styles
    newLayer.Styles = formValues.OverwriteStyles ? selectedStylesPayload : [...newLayer.Styles, ...selectedStylesPayload];

    // Correct the style positions
    newLayer.Styles = newLayer.Styles.map((style, index) => ({ ...style, Position: index }));

    setIsSubmitting(true);
    try {
      if (layerConfig.Type === "SUBLAYER") {
        await updateSubLayerConfig({ sublayer: newLayer as SublayerConfigHydrated, mapLayerConfigId });
      } else {
        await updateLayerConfig({ layerConfig: newLayer as LayerConfigHydrated, mapLayerConfigId });
      }
      onClose();
    } catch (error) {
      enqueueSnackbar("Failed to update layer style", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open
      headerIcon={<ContentCopy />}
      header="Copy styles"
      data-testid="copy-styles-modal"
      variant="primary"
      content={<CopyStylesForm initialValues={formValues} onChange={setFormValues} isSubmitting={isSubmitting} />}
      actions={
        <>
          <Button data-testid="new-style-cancel-button" color="primary" onClick={handleOnCancel} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <LoadingButton
            data-testid="copy-style-save-button"
            color="primary"
            type="submit"
            onClick={handleOnSubmit}
            disabled={formValues.selectedLayerStyles?.length === 0}
            loading={isSubmitting}
          >
            Apply styles
          </LoadingButton>
        </>
      }
    />
  );
};

export default CopyStyles;
